import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu, Tooltip } from '@mantine/core';
import { useMemo } from 'react';
import { useGoogleFiles } from '../../../api/google-integrations-client/google-integrations-client.hooks';
import { DOC_TYPE_CONFIG } from '../../flora/components/flora-document/flora-document.helpers';
import type { Document } from '../../flora/flora.type';
import type { GoogleFileLink } from '../hooks/use-sheet-access-gate';
import { GOOGLE_FILE_TYPE_CONFIG } from './google-file-type.config';

interface AgentFilesMenuProps {
  /** Flora documents the preview rail can show (Word, Excel, plain text), newest first. */
  floraDocuments: Document[];
  /** Google files referenced in the conversation, in appearance order. */
  googleFiles: GoogleFileLink[];
  /** The Flora document the open preview rail is showing, if any. */
  activeFloraDocumentId?: string;
  /** The Google file the open documents rail is showing, if any. */
  activeGoogleFileId?: string;
  /** Whether a file panel (preview or Google) is currently open in the rail. */
  panelOpen: boolean;
  /** Opens the preview rail on a Flora document. */
  onOpenFloraDocument: (documentId: string) => void;
  /** Opens the documents rail on a Google file. */
  onOpenGoogleFile: (fileId: string) => void;
  /** Closes whichever file panel is open. */
  onClosePanel: () => void;
}

/**
 * Header "Files" control for the Flora chat: one button that drops down a list
 * of every file in the conversation (Flora-created or uploaded documents and
 * referenced Google files). Choosing a file opens the side panel on it, so the
 * rail only takes space once the user has picked something to look at.
 *
 * @param {AgentFilesMenuProps} props - The files to list, the active selection, and open/close handlers
 * @returns {JSX.Element | null} The button and its menu, or null when the chat has no files
 */
export function AgentFilesMenu({
  floraDocuments,
  googleFiles,
  activeFloraDocumentId,
  activeGoogleFileId,
  panelOpen,
  onOpenFloraDocument,
  onOpenGoogleFile,
  onClosePanel,
}: AgentFilesMenuProps) {
  const { data: googleListing } = useGoogleFiles(googleFiles.length > 0);
  const googleNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const file of googleListing ?? []) {
      names.set(file.id, file.name);
    }
    return names;
  }, [googleListing]);

  const fileCount = floraDocuments.length + googleFiles.length;
  if (fileCount === 0) return null;

  return (
    <Menu shadow="md" radius="md" position="bottom-end" offset={6} withinPortal={false}>
      <Tooltip label="Files" position="bottom" withArrow>
        <Menu.Target>
          <FilesTrigger type="button" aria-label={`Files (${fileCount})`} data-active={panelOpen}>
            <Icon icon="material-symbols:folder-open-outline" width={16} height={16} />
            <CountBadge aria-hidden="true">{fileCount}</CountBadge>
          </FilesTrigger>
        </Menu.Target>
      </Tooltip>
      <FilesDropdown>
        {floraDocuments.length > 0 && <SectionLabel>Files</SectionLabel>}
        {floraDocuments.map((document) => {
          const config = DOC_TYPE_CONFIG[document.doc_type] ?? DOC_TYPE_CONFIG.pdf;
          const isActive = panelOpen && document.document_id === activeFloraDocumentId;
          return (
            <FileItem
              key={document.document_id}
              aria-current={isActive ? 'true' : undefined}
              data-active={isActive}
              leftSection={<Icon icon={config.icon} width={18} height={18} />}
              rightSection={
                document.revision != null && document.revision > 1 ? (
                  <RevisionTag>v{document.revision}</RevisionTag>
                ) : undefined
              }
              onClick={() => onOpenFloraDocument(document.document_id)}
            >
              <FileLabel>
                <FileName title={document.filename}>{document.filename}</FileName>
                <FileType>{config.label}</FileType>
              </FileLabel>
            </FileItem>
          );
        })}
        {googleFiles.length > 0 && <SectionLabel>Google Drive</SectionLabel>}
        {googleFiles.map((link) => {
          const config = GOOGLE_FILE_TYPE_CONFIG[link.fileType];
          const name = googleNameById.get(link.fileId) ?? config.label;
          const isActive = panelOpen && link.fileId === activeGoogleFileId;
          return (
            <FileItem
              key={link.fileId}
              aria-current={isActive ? 'true' : undefined}
              data-active={isActive}
              leftSection={<Icon icon={config.icon} width={18} height={18} />}
              onClick={() => onOpenGoogleFile(link.fileId)}
            >
              <FileLabel>
                <FileName title={name}>{name}</FileName>
                <FileType>{config.label}</FileType>
              </FileLabel>
            </FileItem>
          );
        })}
        {panelOpen && (
          <>
            <Menu.Divider />
            <FileItem
              leftSection={<Icon icon="material-symbols:close" width={16} height={16} />}
              onClick={onClosePanel}
            >
              Close preview
            </FileItem>
          </>
        )}
      </FilesDropdown>
    </Menu>
  );
}

const FilesTrigger = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover,
  &[aria-expanded='true'] {
    border-color: var(--sky-base);
    color: var(--ink-darkest);
    background: var(--sky-lighter);
  }

  &[data-active='true'] {
    border-color: var(--secondary-base);
    color: var(--secondary-base);
    background: var(--secondary-lightest);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const CountBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--secondary-base);
  color: var(--base-white);
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
`;

const FilesDropdown = styled(Menu.Dropdown)`
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
  box-shadow: var(--box-shadow-big-soft);
  padding: 6px;
  width: 300px;
  max-height: 420px;
  overflow-y: auto;
`;

const SectionLabel = styled.div`
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--ink-lighter);
  user-select: none;
`;

// Mantine paints hover, keyboard focus and [data-menu-active] from
// --menu-item-hover; pointing it at a theme token keeps the highlight readable
// in Flora dark mode, where Mantine itself still runs its light scheme.
const FileItem = styled(Menu.Item)`
  --menu-item-hover: var(--sky-lighter);
  padding: 6px 10px;
  border-radius: 6px;
  color: var(--ink-dark);

  &[data-active='true'] {
    background: var(--secondary-lightest);
  }
`;

const FileLabel = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FileName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileType = styled.span`
  font-size: 11px;
  color: var(--ink-lighter);
`;

const RevisionTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-lighter);
`;
